import React, { useState, useEffect } from 'react';
import ErrorWrapper from '@/app/ErrorWrapper';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Spinner } from 'reactstrap';
import { notification, Table } from 'antd'; // 'notification' is already imported
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { convertRoutePathToLink, dateStringToYYYYMMDD } from '@/utils/functions';
import { routesPath } from '@/utils/routesPath';
import { programsApi } from '@/api';
import { useTranslation } from 'react-i18next'; // Added import

const URL = convertRoutePathToLink(routesPath.PROGRAMS);

const EditComponent = ({ ...props }) => {
    const { t } = useTranslation(); // Added hook
    const [searchParams, setSearchParams] = useSearchParams();
    const { handleSubmit, control, reset } = useForm();
    const navigate = useNavigate();
    const id = searchParams.get('id');
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const getDetail = () => {
        // --- ADDED --- Guard clause if no ID
        if (!id) return;
        setIsLoadingDetail(true);
        programsApi
            .getDetail(id)
            .then((res) => {
                const detail = res.data.data;
                reset({
                    // Reset with fetched data
                    name: detail?.name,
                    description: detail?.description
                });
            })
            // --- UPDATED --- Add error notification
            .catch((err) => {
                console.error('Get program detail error:', err);
                notification.error({
                    message: t('Program_Admin_Edit_Load_Failed_Message'),
                    description:
                        err.response?.data?.message ||
                        t('Program_Admin_Edit_Load_Failed_Description')
                });
            })
            // --- ADDED --- Finally block
            .finally(() => {
                setIsLoadingDetail(false);
            });
    };

    useEffect(() => {
        if (id) {
            getDetail();
        } else {
            // --- ADDED --- Reset form for create mode
            reset({ name: '', description: '' });
        }
    }, [id]); // Depend only on id

    const onFormError = (errors) => {
        console.error('Form validation errors:', errors);
        notification.warning({
            message: t('Program_Admin_Edit_Validation_MissingFields_Message'),
            description: t('Program_Admin_Edit_Validation_MissingFields_Description')
        });
    };

    const submitForm = (formPayload) => {
        setIsLoadingDetail(true);
        const apiCall = id
            ? programsApi.update({ ...formPayload, id: id })
            : programsApi.create({ ...formPayload });

        const successMessage = id
            ? t('Program_Admin_Edit_Submit_UpdateSuccess_Message')
            : t('Program_Admin_Edit_Submit_CreateSuccess_Message');
        const failMessage = id
            ? t('Program_Admin_Edit_Submit_UpdateFailed_Message')
            : t('Program_Admin_Edit_Submit_CreateFailed_Message');

        apiCall
            .then((res) => {
                notification.success({ message: successMessage });
                if (!id) {
                    navigate(URL); // Navigate back only on creation
                } else {
                    // Optional: re-fetch details after update if needed,
                    // but usually not required if staying on the same page.
                    // getDetail();
                }
            })
            .catch((err) => {
                notification.error({
                    message: failMessage,
                    description:
                        err.response?.data?.message ||
                        t('Program_Admin_Edit_Submit_Failed_Description')
                });
                console.error(err);
            })
            .finally(() => {
                setIsLoadingDetail(false);
            });
    };

    return (
        <>
            <div className="section-body">
                <div className="container-fluid">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="header-action">
                            {/* --- UPDATED --- Dynamic page title */}
                            <h1 className="page-title">
                                {id
                                    ? t('Program_Admin_Edit_PageTitle_Edit')
                                    : t('Program_Admin_Edit_PageTitle_Create')}
                            </h1>
                            <ol className="breadcrumb page-breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="#">{t('Program_Admin_Edit_Breadcrumb_Brand')}</a>
                                </li>
                                {/* --- UPDATED --- Link back to programs list */}
                                <li className="breadcrumb-item">
                                    <Link to={URL}>{t('Program_Admin_Edit_Breadcrumb_Programs')}</Link>
                                </li>
                                <li
                                    className="breadcrumb-item active"
                                    aria-current="page">
                                    {/* --- UPDATED --- Dynamic text */}
                                    {id
                                        ? t('Program_Admin_Edit_Breadcrumb_Active_Edit')
                                        : t('Program_Admin_Edit_Breadcrumb_Active_Create')}
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div className="section-body mt-4">
                {/* Show loading only when fetching details for edit */}
                {isLoadingDetail && id ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            minHeight: '300px' // Increased height
                        }}>
                        <Spinner style={{ width: '3rem', height: '3rem' }} />{' '}
                        {/* Larger spinner */}
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit(submitForm, onFormError)}
                        className="flex">
                        {' '}
                        {/* Removed flex unless needed */}
                        <div className="col-12">
                            {' '}
                            {/* Use full width */}
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">
                                        {t('Program_Admin_Edit_CardTitle_BasicInfo')}
                                    </h3>
                                </div>
                                <div className="card-body">
                                    <div className="row clearfix">
                                        <div className="col-md-6 col-sm-12">
                                            {' '}
                                            {/* Adjusted column width */}
                                            <div className="form-group">
                                                {/* --- UPDATED --- Added required asterisk */}
                                                <label>
                                                    {t('Program_Admin_Edit_Form_Name')}{' '}
                                                    <span className="text-danger">*</span>
                                                </label>
                                                <Controller
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            className="form-control"
                                                            placeholder={t(
                                                                'Program_Admin_Edit_Form_Name_Placeholder'
                                                            )} // Added placeholder
                                                        />
                                                    )}
                                                    name="name"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-12">
                                            <div className="form-group">
                                                {/* --- UPDATED --- Added required asterisk */}
                                                <label>
                                                    {t('Program_Admin_Edit_Form_Description')}{' '}
                                                    <span className="text-danger">*</span>
                                                </label>
                                                <Controller
                                                    control={control}
                                                    name="description"
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <textarea
                                                            rows="5" // Increased rows
                                                            className="form-control no-resize"
                                                            placeholder={t(
                                                                'Program_Admin_Edit_Form_Description_Placeholder'
                                                            )} // Added placeholder
                                                            {...field}></textarea>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Moved buttons outside card-body but inside card */}
                                <div className="mt-3 p-3">
                                    {' '}
                                    {/* Added margin top */}
                                    <button
                                        type="submit"
                                        className="mr-1 btn btn-primary"
                                        disabled={isLoadingDetail}>
                                        {' '}
                                        {/* Disable on loading */}
                                        {isLoadingDetail
                                            ? t('Program_Admin_Edit_Button_Saving')
                                            : t('Program_Admin_Edit_Button_Submit')}{' '}
                                        {/* Loading text */}
                                    </button>
                                    {/* Added Cancel button */}
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() =>
                                            navigate(URL)
                                        } // Navigate back to list
                                        disabled={isLoadingDetail}>
                                        {t('Program_Admin_Edit_Button_Cancel')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </>
    );
};

EditComponent.displayName = 'Edit';

const Edit = (props) => {
    return (
        <ErrorWrapper>
            <EditComponent
                data-id="Edit-cpn"
                {...props}
            />
        </ErrorWrapper>
    );
};

export default Edit;